import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import ProLayout, { type BasicLayoutProps } from '@ant-design/pro-layout';
import { Grid, Typography } from 'antd';
import { isEmpty } from 'lodash/fp';
import React, { Suspense, useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useCompanyContext } from '../../components/contexts/CompanyContextManager';
import LoadingElement from '../../components/LoadingElement';
import breakpoints from '../../utilities/breakpoints';
import { clearAllSortAndFilter } from '../../utilities/useSortAndFilterCache';
import ErrorBoundary from '../ErrorBoundary';
import HeaderLogo from '../HeaderLogo';
import ConsoleRightContent from './ConsoleRightContent';
import useConsoleMenu from './useConsoleMenu';

const StyledProLayout = styled(ProLayout)`
    aside > .ant-layout-sider-children {
        padding-bottom: 2rem;
    }
    .ant-menu-inline > .ant-menu-item,
    .ant-menu.ant-menu-inline-collapsed > .ant-menu-item {
        // to accommodate the 1px for border
        height: 46px;
        line-height: 46px;
        overflow: visible;

        // we remove the margin and increase the height instead
        // to create space between elements
        // this is because the border can only be added in the child component
        // where spacing will not be same if there is margin
        margin-top: 0;
        margin-bottom: 0;
    }
`;

export const StyledMenuContainer = styled.div<{ isLast?: boolean }>`
    ${({ isLast }) =>
        isLast &&
        `

  `}
    height: 46px;
    line-height: 46px;
    border-top: 1px solid rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
`;

export const StyledMenuFoldOutlined = styled(MenuFoldOutlined)`
    color: #000000;
    font-size: 18px;
    &:hover {
        color: var(--ant-primary-color);
    }
`;

export const StyledMenuUnfoldOutlined = styled(MenuUnfoldOutlined)`
    color: #000000;
    font-size: 18px;
    &:hover {
        color: var(--ant-primary-color);
    }
    @media screen and (max-width: ${breakpoints.md}) {
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

export type ConsoleLayoutProps = {
    children: JSX.Element;
};

const headers = new Set(['contents', 'console-applications', 'settings']);

const itemRender = route => <Link to={route.path}>{route.breadcrumbName}</Link>;

const headerContentRender = () => <HeaderLogo link="/admin" />;

const rightContentRender = () => <ConsoleRightContent />;

const Title = styled(Typography.Title)`
    & .ant-pro-menu-item {
        display: flex;
        align-items: center;
    }
`;

const ConsoleLayout = ({ children }: ConsoleLayoutProps) => {
    const [collapsed, setCollapsed] = useState(false);
    const location = useLocation();
    const screens = Grid.useBreakpoint();
    const leftContentRender = collapsed ? (
        <StyledMenuUnfoldOutlined className="trigger" onClick={() => setCollapsed(!collapsed)} />
    ) : (
        <StyledMenuFoldOutlined className="trigger" onClick={() => setCollapsed(!collapsed)} />
    );

    const { userDataFromCompanies } = useCompanyContext();

    const menuHeaderRender = () => {
        if (screens.md) {
            return null;
        }

        return collapsed ? (
            <StyledMenuUnfoldOutlined className="trigger" onClick={() => setCollapsed(!collapsed)} />
        ) : (
            <StyledMenuFoldOutlined className="trigger" onClick={() => setCollapsed(!collapsed)} />
        );
    };

    const menuItemRender = useCallback<Exclude<BasicLayoutProps['menuItemRender'], undefined | false>>(
        (menuItemProps, defaultDom) => {
            if (menuItemProps.isUrl || menuItemProps.children) {
                return defaultDom;
            }

            if (menuItemProps.path && location.pathname !== menuItemProps.path && !headers.has(menuItemProps.key)) {
                return (
                    <div>
                        <Link target={menuItemProps.target} to={menuItemProps.path}>
                            {defaultDom}
                        </Link>
                    </div>
                );
            }
            if (
                headers.has(menuItemProps.key) &&
                (menuItemProps.key === 'contents' ||
                    menuItemProps.key === 'settings' ||
                    menuItemProps.key === 'console-applications')
            ) {
                return (
                    <StyledMenuContainer>
                        <Title level={5} style={{ marginBottom: 0 }}>
                            {' '}
                            {defaultDom}
                        </Title>
                    </StyledMenuContainer>
                );
            }

            return defaultDom;
        },
        [location.pathname]
    );

    const handleMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        const clickedElement = event.target as HTMLElement;

        const getParentList = (element: HTMLElement) => {
            /*
            This function is to getting the parent <li> element, this is required because
            the event that we get from onClickCapture is based on the element that we click, it might be the <svg> icon,
            or the <span> of the submenu. So whathever we click, it must referring back to the parent <li> element.

            Why using onClickCapture instead of onClick, because the onClick only returning the event if there're
            actions triggered by clicking, such as clicking the menu with <a> tag,
            But it doesn't returning anything if clicking the menu that has child because it doesn't do any action
            (But it changed the class tho, to show the children list menu, I'm not sure why :/)
            */
            if (!element || element.tagName === 'UL') {
                return null;
            }
            if (element.tagName === 'LI') {
                return element;
            }

            return getParentList(element.parentElement);
        };

        const getParentContainerElement = () =>
            document.getElementsByClassName('ant-layout-sider-children')[0]?.firstElementChild;

        const parentListElement = getParentList(clickedElement);
        const parentContainerElement = getParentContainerElement() as HTMLElement;

        if (
            parentListElement &&
            parentContainerElement &&
            parentListElement.classList.contains('ant-menu-submenu') &&
            !parentListElement.classList.contains('ant-menu-submenu-open')
        ) {
            /*
                This timeout to give some spare time for submenu element to be rendered,
                because if not, the scroll wont work. Currently the sweetspot is on 300ms
            */

            const { clientHeight: maxClientViewHeight, scrollTop: containerScrollTop } = parentContainerElement;

            setTimeout(() => {
                const { lastElementChild } = parentListElement;
                if (lastElementChild) {
                    const { height: childElementHeight, bottom: childElementBottomPosition } =
                        lastElementChild.getBoundingClientRect();
                    if (childElementBottomPosition > maxClientViewHeight) {
                        parentContainerElement.scrollTo({
                            top: containerScrollTop + childElementHeight,
                            behavior: 'smooth',
                        });
                    }
                }
            }, 300);
        }
    }, []);

    const rootRoute = useConsoleMenu(collapsed);

    /*
        If userDataFromCompanies still empty, meaning the company fetch is not completed yet
        Hence, don't show the menu and just show a loading
    */

    return userDataFromCompanies ? (
        <StyledProLayout
            collapsed={collapsed}
            collapsedButtonRender={null}
            headerContentRender={headerContentRender}
            headerHeight={80}
            headerTheme="light"
            itemRender={itemRender}
            layout="mix"
            location={location}
            logo={leftContentRender}
            menuHeaderRender={menuHeaderRender}
            menuItemRender={menuItemRender}
            menuProps={{
                onSelect: clearAllSortAndFilter,
                onClickCapture: handleMenuClick,
            }}
            navTheme="light"
            onCollapse={setCollapsed}
            onPageChange={() => {
                if (isEmpty(screens)) {
                    return undefined;
                }

                return !screens?.md ? setCollapsed(!collapsed) : undefined;
            }}
            primaryColor="#1890ff"
            rightContentRender={rightContentRender}
            route={rootRoute}
            title={false}
            fixSiderbar
            fixedHeader
        >
            <Suspense fallback={<LoadingElement />}>
                <ErrorBoundary>{children}</ErrorBoundary>
            </Suspense>
        </StyledProLayout>
    ) : (
        <LoadingElement />
    );
};

export default ConsoleLayout;
